<template>
  <div class="refresh-interval-override-dropdown">
    <button
      ref="buttonRef"
      class="btn-notification"
      :class="{ 'is-enabled': hasOverride, 'is-open': isOpen }"
      @click="toggleDropdown"
    >
      <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="btn-icon" />
      <span class="btn-text">
        <span class="btn-label">Refresh</span>
        <span class="btn-value">{{ displayLabel }}</span>
      </span>
      <font-awesome-icon :icon="['fas', 'chevron-down']" class="btn-chevron" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="override-dropdown-menu"
        :style="dropdownStyles"
      >
        <div
          v-for="option in refreshIntervalOptions"
          :key="option.value"
          class="frequency-option"
          :class="{ 'is-selected': globalRefreshInterval === option.value }"
          @click="selectRefreshInterval(option.value)"
        >
          <span class="option-check-space">
            <font-awesome-icon v-if="globalRefreshInterval === option.value" :icon="['fas', 'check']" class="option-check" />
          </span>
          <span class="option-label">{{ option.label }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useGlobalDashboardSettings } from '@/composables/useGlobalDashboardSettings'
import { GLOBAL_REFRESH_INTERVAL_OPTIONS } from '@/config/dashboardOptions'

// Refresh interval options from shared constants
const refreshIntervalOptions = GLOBAL_REFRESH_INTERVAL_OPTIONS

const isOpen = ref(false)
const buttonRef = ref(null)
const dropdownStyles = ref({})

const { globalRefreshInterval, hasRefreshOverride, setGlobalRefreshInterval } = useGlobalDashboardSettings()

const displayLabel = computed(() => {
  const option = refreshIntervalOptions.find(o => o.value === globalRefreshInterval.value)
  return option ? option.label : 'Per Widget'
})

const hasOverride = computed(() => hasRefreshOverride.value)

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    calculateDropdownPosition()
  }
}

function selectRefreshInterval(value) {
  setGlobalRefreshInterval(value)
  isOpen.value = false
}

function calculateDropdownPosition() {
  if (!buttonRef.value) return

  const rect = buttonRef.value.getBoundingClientRect()
  const dropdownHeight = 350 // Approximate
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top

  if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
    // Position below
    dropdownStyles.value = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  } else {
    // Position above
    dropdownStyles.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 8}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  }
}

function handleClickOutside(event) {
  if (isOpen.value && buttonRef.value && !buttonRef.value.contains(event.target)) {
    const dropdown = document.querySelector('.override-dropdown-menu')
    if (dropdown && !dropdown.contains(event.target)) {
      isOpen.value = false
    }
  }
}

function handleEscape(event) {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false
  }
}

function handleResize() {
  if (isOpen.value) {
    calculateDropdownPosition()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleResize)
})
</script>

<style scoped>
.refresh-interval-override-dropdown {
  position: relative;
  display: inline-flex;
}

.btn-notification {
  padding: 0.625rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #9CA3AF;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-notification:hover:not(:disabled) {
  background: rgba(139, 92, 246, 0.12);
  border-color: rgba(139, 92, 246, 0.25);
  color: #E5E7EB;
}

.btn-notification.is-enabled {
  background: rgba(139, 92, 246, 0.08);
  border-color: rgba(139, 92, 246, 0.2);
  color: #E5E7EB;
}

.btn-notification.is-enabled .btn-icon {
  color: #a78bfa;
}

.btn-notification.is-open {
  background: rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.35);
}

.btn-icon {
  font-size: 0.875rem;
  flex-shrink: 0;
  width: 1rem;
  text-align: center;
}

.btn-text {
  white-space: nowrap;
}

.btn-label {
  color: #9CA3AF;
  font-weight: 400;
}

.btn-value {
  color: #fff;
  font-weight: 600;
  margin-left: 0.25rem;
}

.btn-notification:not(.is-enabled) .btn-value {
  color: #6B7280;
  font-weight: 500;
}

.btn-chevron {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 0.125rem;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.btn-notification.is-open .btn-chevron {
  transform: rotate(180deg);
}

/* Dropdown menu */
.override-dropdown-menu {
  background: #1a1f28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.625rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  z-index: 9999;
  overflow: hidden;
  padding: 0.5rem 0;
}

.frequency-option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.frequency-option:hover {
  background: rgba(255, 255, 255, 0.04);
}

.frequency-option.is-selected {
  background: rgba(139, 92, 246, 0.08);
}

.frequency-option.is-selected:hover {
  background: rgba(139, 92, 246, 0.12);
}

.option-check-space {
  width: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.option-check {
  font-size: 0.75rem;
  color: #a78bfa;
}

.option-label {
  font-size: 0.875rem;
  color: #E5E7EB;
}
</style>
